import React from 'react';
// eslint-disable-next-line no-unused-vars
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
    title = 'TechCognita - Empowering Minds, Innovating the Future',
    description = 'TechCognita provides comprehensive technology education and consulting services. Learn programming, web development, and more from industry experts.',
    keywords = 'programming courses, web development, mobile development, IT training, technology education, coding bootcamp',
    image = 'https://techcognita.com/TechCognita_Logo.jpg',
    url = 'https://techcognita.com',
    type = 'website',
    author = 'TechCognita',
    publishedTime = new Date().toISOString(),
    modifiedTime = new Date().toISOString(),
    section = 'Technology Education',
    tags = ['programming', 'web development', 'IT training'],
    locale = 'en_US',
    alternateLocales = ['en_GB', 'en_IN'],
    noIndex = false,
    noFollow = false,
    breadcrumbs = []
}) => {
    const siteTitle = 'TechCognita';
    const fullTitle = title === siteTitle ? title : `${title} | ${siteTitle}`;

    // Generate structured data for the page
    const structuredData = {
        "@context": "https://schema.org",
        "@type": type === 'article' ? "Article" : "WebPage",
        "headline": fullTitle,
        "description": description,
        "image": image,
        "url": url,
        "publisher": {
            "@type": "Organization",
            "name": "TechCognita",
            "logo": {
                "@type": "ImageObject",
                "url": "https://techcognita.com/logo.png"
            }
        },
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": url
        }
    };

    // Add breadcrumb structured data if available
    if (breadcrumbs.length > 0) {
        structuredData.breadcrumb = {
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs.map((item, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "name": item.name,
                "item": item.url
            }))
        };
    }

    if (type === 'article') {
        structuredData.author = {
            "@type": "Organization",
            "name": author
        };
        structuredData.datePublished = publishedTime;
        structuredData.dateModified = modifiedTime;
        structuredData.articleSection = section;
        structuredData.keywords = tags.join(', ');
    }

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            <meta name="author" content={author} />
            <meta name="theme-color" content="#4F46E5" />

            {/* Robots Meta */}
            <meta name="robots" content={`${noIndex ? 'noindex' : 'index'},${noFollow ? 'nofollow' : 'follow'}`} />

            {/* Open Graph Tags */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={url} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            <meta property="og:site_name" content="TechCognita" />
            <meta property="og:locale" content={locale} />

            {/* Twitter Card Tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={url} />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {/* Canonical URL */}
            <link rel="canonical" href={url} />

            {/* Alternate Languages */}
            {alternateLocales.map(locale => (
                <link rel="alternate" hrefLang={locale} href={`${url}?lang=${locale}`} key={locale} />
            ))}

            {/* Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify(structuredData)}
            </script>

            {/* Additional Meta Tags */}
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
            <meta name="language" content="English" />
            <meta name="revisit-after" content="7 days" />
            <meta name="rating" content="general" />

            {/* Preconnect to external domains */}
            <link rel="preconnect" href="https://www.google-analytics.com" />
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        </Helmet>
    );
};

export default SEO; 